import { useCallback, useContext, useSyncExternalStore } from 'react';
import { QueryClientContext, type QueryClient } from '@tanstack/react-query';
import {
  redoActivityResponseSchema,
  undoActivityResponseSchema,
  type ActivityEntry,
  type OwnProfile,
  type RedoActivityResponse,
  type UndoActivityResponse,
} from '@molt/shared';

import { post } from './api';
import { announce } from './announce';
import type { LocaleFormattingTranslate } from './locale-formatting';
import { getBoardActivity, getCardActivity } from './activity';
import { queryKeys } from './query-keys';

export type UndoSourceScope =
  | { readonly kind: 'card'; readonly cardId: string }
  | { readonly kind: 'board'; readonly boardId: string };

export interface UndoSourceRegistration {
  readonly boardId: string;
  readonly cardId?: string | undefined;
  readonly activityEntryId?: string | undefined;
  readonly scope: UndoSourceScope;
  readonly eventTypes: readonly string[];
  readonly label: string;
  readonly sourceKey?: string | undefined;
}

export interface UndoToastState {
  readonly message: string;
  readonly actionLabel: string;
  /**
   * FE-80.1 - WHICH stack entry this toast is about, so that
   * `clearUndoSource` can ask that question directly.
   *
   * It used to ask it by rebuilding all three English sentences from the
   * entry's label and comparing them to the rendered toast. That was pure
   * while every sentence was a module literal. Once the frame goes through
   * `t`, prose equality holds only for as long as both sides are built from
   * the SAME catalogue - a comparison that is silently language-dependent, and
   * one that would start returning false mid-session if the catalogue resolved
   * between the push and the clear. An id compares the same in every language.
   */
  readonly entryId: string;
}

interface UndoStackEntry {
  readonly activityEntryId: string;
  readonly boardId: string;
  readonly cardId?: string | undefined;
  readonly sourceKey?: string | undefined;
  readonly label: string;
}

interface BoardUndoState {
  undoStack: UndoStackEntry[];
  redoStack: UndoStackEntry[];
  undoBusy: boolean;
  redoBusy: boolean;
  toast: UndoToastState | null;
}

interface StoredBoardUndoState {
  readonly undoStack: readonly UndoStackEntry[];
  readonly redoStack: readonly UndoStackEntry[];
}

const MAX_STACK_DEPTH = 50;
const STORAGE_PREFIX = 'molt:undo-redo:';
const CHANGE_EVENT = 'molt:undo-redo-change';
const boardStates = new Map<string, BoardUndoState>();
const revisions = new Map<string, number>();
const serverListeners = new Map<string, Set<() => void>>();

export function undoActivity(activityEntryId: string): Promise<UndoActivityResponse> {
  return post(
    `/activity/${encodeURIComponent(activityEntryId)}/undo`,
    undoActivityResponseSchema,
    {},
  );
}

export function redoActivity(activityEntryId: string): Promise<RedoActivityResponse> {
  return post(
    `/activity/${encodeURIComponent(activityEntryId)}/redo`,
    redoActivityResponseSchema,
    {},
  );
}

/**
 * FE-80.1 - `t` is a REQUIRED second parameter, not a defaulted one and not a
 * `useInterfaceLanguage()` call inside the hook.
 *
 * `lib/dnd.ts` and `lib/card-title.ts` record the reason: a defaulted
 * translator lets one call site fall through to a default that looks correct,
 * and only a required parameter makes `tsc` enumerate the callers. There are
 * two (`BoardFace.tsx`, `CardBack.tsx`) plus one test harness, and this slice
 * wants all three named by the compiler rather than found in a screenshot.
 *
 * `t` is passed DOWN to `runUndo`/`runRedo` at call time and never captured
 * into `boardStates`. The module state outlives any one render and is shared
 * across every hook instance on the board; a translator stored there would be
 * whichever render pushed first, which is exactly the stale-catalogue defect
 * the argument-passing avoids.
 */
export function useUndoRedo(
  boardId: string,
  t: LocaleFormattingTranslate,
): {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoBusy: boolean;
  readonly redoBusy: boolean;
  readonly toast: UndoToastState | null;
  readonly recordSource: (source: UndoSourceRegistration) => Promise<void>;
  readonly clearSource: (sourceKey: string) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly dismissToast: () => void;
} {
  const queryClient = useContext(QueryClientContext);
  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeBoard(boardId, onStoreChange),
    [boardId],
  );
  const getSnapshot = useCallback(() => revisions.get(boardId) ?? 0, [boardId]);
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const state = getBoardState(boardId);

  const recordSource = useCallback(
    async (source: UndoSourceRegistration) => {
      const targetState = getBoardState(source.boardId);
      targetState.redoStack = [];
      targetState.toast = null;
      persistBoardState(source.boardId);
      notifyBoard(source.boardId);

      if (source.activityEntryId !== undefined) {
        pushUndoStackEntry(targetState, t, {
          activityEntryId: source.activityEntryId,
          boardId: source.boardId,
          ...(source.cardId === undefined ? {} : { cardId: source.cardId }),
          ...(source.sourceKey === undefined ? {} : { sourceKey: source.sourceKey }),
          label: source.label,
        });
        persistBoardState(source.boardId);
        notifyBoard(source.boardId);
        return;
      }

      await trackPendingSource(
        source.boardId,
        discoverUndoSource(source, targetState, t, queryClient),
      );
    },
    /*
     * `t` belongs here and is safe here, and the two facts have one cause:
     * `InterfaceLanguageProvider` memoises `t` on the resolved catalogue
     * (`interface-language.tsx`), so this callback is rebuilt when the
     * catalogue changes and at no other time.
     */
    [queryClient, t],
  );

  const clearSource = useCallback(
    (sourceKey: string) => {
      clearUndoSource(boardId, sourceKey);
    },
    [boardId],
  );

  const undo = useCallback(() => {
    void runUndo(boardId, queryClient, t);
  }, [boardId, queryClient, t]);

  const redo = useCallback(() => {
    void runRedo(boardId, queryClient, t);
  }, [boardId, queryClient, t]);

  const dismissToast = useCallback(() => {
    const targetState = getBoardState(boardId);
    targetState.toast = null;
    notifyBoard(boardId);
  }, [boardId]);

  return {
    canUndo: state.undoStack.length > 0 && !state.undoBusy && !state.redoBusy,
    canRedo: state.redoStack.length > 0 && !state.undoBusy && !state.redoBusy,
    undoBusy: state.undoBusy,
    redoBusy: state.redoBusy,
    toast: state.toast,
    recordSource,
    clearSource,
    undo,
    redo,
    dismissToast,
  };
}

export function listUndoSourceKey(listId: string): string {
  return `list:${listId}`;
}

export function clearUndoSource(boardId: string, sourceKey: string): void {
  const state = getBoardState(boardId);
  const removedEntries = [...state.undoStack, ...state.redoStack].filter(
    (entry) => entry.sourceKey === sourceKey,
  );
  const undoStack = state.undoStack.filter((entry) => entry.sourceKey !== sourceKey);
  const redoStack = state.redoStack.filter((entry) => entry.sourceKey !== sourceKey);
  const changed =
    undoStack.length !== state.undoStack.length ||
    redoStack.length !== state.redoStack.length;
  if (!changed) return;

  /*
   * Compared on `activityEntryId`, where this rebuilt three English sentences
   * from `entry.label` and compared them to the rendered toast.
   *
   * That worked only because every sentence was a module literal. The frame is
   * routed now, so prose equality would hold only while both sides came from
   * one catalogue - true today by luck of the call graph, and false the moment
   * the catalogue resolves between the push and the clear. The comparison also
   * had to know all three frames, so a fourth toast sentence would have been a
   * silent miss rather than a compile error.
   *
   * This is why `clearUndoSource` keeps its two-parameter signature while every
   * sibling gained a `t`: routing REMOVED this function's need for one.
   */
  const removedToast =
    state.toast !== null &&
    removedEntries.some((entry) => entry.activityEntryId === state.toast?.entryId);

  state.undoStack = undoStack;
  state.redoStack = redoStack;
  if (removedToast) state.toast = null;
  persistBoardState(boardId);
  notifyBoard(boardId);
}

/**
 * THE DISCOVERY WINDOW, and the defect that made it visible.
 *
 * A registration that arrives WITHOUT an `activityEntryId` cannot push
 * anything until it has round-tripped the activity feed and found the row the
 * member just created. That round trip is fast, but it is not instant, and the
 * gap it leaves is exactly the moment `FR-EXP-013` AC1 describes: "given the
 * user just dragged a card to another list, when they press `Z`".
 *
 * Measured in the browser, not reasoned about. `apps/web/e2e/FR-EXP-013.spec.ts`
 * drives a keyboard drag and presses `Z` the instant the card lands. Before
 * this map existed the shell live region answered the empty-stack refusal
 * while `sessionStorage` already held the entry a beat later - the product
 * told a screen-reader user there was nothing to undo about the move they had
 * just made, and then quietly became able to undo it. Nothing was red: the
 * board face`s toolbar button is driven by the same stack, so it was merely
 * disabled for the same few hundred milliseconds, and no unit test can see a
 * window that only a real request opens.
 *
 * So `runUndo` and `runRedo` WAIT for a registration that is still resolving
 * instead of reading an empty stack and announcing a refusal that is about to
 * become false. They wait only when something is genuinely in flight: with the
 * map empty neither function awaits at all, so the fully synchronous path that
 * the busy guard depends on is unchanged.
 */
const pendingSources = new Map<string, Promise<void>>();

function trackPendingSource(boardId: string, work: Promise<void>): Promise<void> {
  const previous = pendingSources.get(boardId);
  const chained: Promise<void> = (
    previous === undefined
      ? work
      : previous.then(
          () => work,
          () => work,
        )
  ).then(
    () => undefined,
    () => undefined,
  );
  pendingSources.set(boardId, chained);
  void chained.then(() => {
    if (pendingSources.get(boardId) === chained) pendingSources.delete(boardId);
  });
  return chained;
}

/**
 * Loops rather than awaiting once: a second action recorded while the first is
 * still resolving replaces the map entry, and a single await would return with
 * that newer registration still in flight. The bound is a guard against a
 * pathological stream of registrations, not an expected path.
 */
async function settlePendingSources(boardId: string): Promise<void> {
  for (let guard = 0; guard < 8; guard += 1) {
    const pending = pendingSources.get(boardId);
    if (pending === undefined) return;
    await pending;
  }
}

async function discoverUndoSource(
  source: UndoSourceRegistration,
  targetState: BoardUndoState,
  t: LocaleFormattingTranslate,
  queryClient: QueryClient | undefined,
): Promise<void> {
  const feed = await getUndoSourceFeed(source);
  const currentUser = queryClient?.getQueryData<OwnProfile>(queryKeys.me());
  const entry = newestMatchingActivity(feed.entries, source.eventTypes, currentUser);

  if (entry === null) {
    warnUndoSourceMiss(source);
    return;
  }

  const pushed: UndoStackEntry = {
    activityEntryId: entry.id,
    boardId: source.boardId,
    ...(source.cardId === undefined ? {} : { cardId: source.cardId }),
    ...(source.sourceKey === undefined ? {} : { sourceKey: source.sourceKey }),
    label: source.label,
  };
  pushUndoStackEntry(targetState, t, pushed);
  persistBoardState(source.boardId);
  notifyBoard(source.boardId);
}

function newestMatchingActivity(
  entries: readonly ActivityEntry[],
  eventTypes: readonly string[],
  currentUser: OwnProfile | undefined,
): ActivityEntry | null {
  const matchingType = entries.filter((entry) => eventTypes.includes(entry.type));
  if (currentUser === undefined) return matchingType[0] ?? null;
  return matchingType.find((entry) => entry.actor?.id === currentUser.id) ?? null;
}

function pushUndoStackEntry(
  state: BoardUndoState,
  t: LocaleFormattingTranslate,
  entry: UndoStackEntry,
): void {
  state.undoStack = [...state.undoStack, entry].slice(-MAX_STACK_DEPTH);
  state.redoStack = [];
  state.toast = {
    message: recordedMessage(t, entry.label),
    actionLabel: t('undo.action.undo'),
    entryId: entry.activityEntryId,
  };
}

async function runUndo(
  boardId: string,
  queryClient: QueryClient | undefined,
  t: LocaleFormattingTranslate,
): Promise<void> {
  if (pendingSources.has(boardId)) await settlePendingSources(boardId);
  const state = getBoardState(boardId);
  if (state.undoBusy || state.redoBusy) {
    announce(t('undo.busy.undo'));
    return;
  }

  const entry = state.undoStack.at(-1);
  if (entry === undefined) {
    announce(t('undo.empty.undo'));
    return;
  }

  state.undoBusy = true;
  notifyBoard(boardId);
  try {
    const receipt = await undoActivity(entry.activityEntryId);
    removeEntry(state.undoStack, entry);
    state.redoStack = [...state.redoStack, entry].slice(-MAX_STACK_DEPTH);
    const message = completionMessage(t, entry.label, 'undone');
    state.toast = {
      message,
      actionLabel: t('undo.action.redo'),
      entryId: entry.activityEntryId,
    };
    persistBoardState(boardId);
    await invalidateUndoReceipt(queryClient, receipt, entry);
    announce(message);
  } catch (error) {
    announce(errorMessage(error, t('undo.error.undo')));
  } finally {
    state.undoBusy = false;
    notifyBoard(boardId);
  }
}

async function runRedo(
  boardId: string,
  queryClient: QueryClient | undefined,
  t: LocaleFormattingTranslate,
): Promise<void> {
  if (pendingSources.has(boardId)) await settlePendingSources(boardId);
  const state = getBoardState(boardId);
  if (state.undoBusy || state.redoBusy) {
    announce(t('undo.busy.redo'));
    return;
  }

  const entry = state.redoStack.at(-1);
  if (entry === undefined) {
    announce(t('undo.empty.redo'));
    return;
  }

  state.redoBusy = true;
  notifyBoard(boardId);
  try {
    const receipt = await redoActivity(entry.activityEntryId);
    removeEntry(state.redoStack, entry);
    state.undoStack = [...state.undoStack, entry].slice(-MAX_STACK_DEPTH);
    const message = completionMessage(t, entry.label, 'redone');
    state.toast = {
      message,
      actionLabel: t('undo.action.undo'),
      entryId: entry.activityEntryId,
    };
    persistBoardState(boardId);
    await invalidateUndoReceipt(queryClient, receipt, entry);
    announce(message);
  } catch (error) {
    announce(errorMessage(error, t('undo.error.redo')));
  } finally {
    state.redoBusy = false;
    notifyBoard(boardId);
  }
}

function removeEntry(stack: UndoStackEntry[], entry: UndoStackEntry): void {
  const top = stack.at(-1);
  if (top?.activityEntryId === entry.activityEntryId) {
    stack.pop();
    return;
  }
  const index = stack.findLastIndex(
    (item) => item.activityEntryId === entry.activityEntryId,
  );
  if (index !== -1) stack.splice(index, 1);
}

async function invalidateUndoReceipt(
  queryClient: QueryClient | undefined,
  receipt: UndoActivityResponse | RedoActivityResponse,
  fallback: UndoStackEntry,
): Promise<void> {
  if (queryClient === undefined) return;
  const cardId = receipt.cardId ?? fallback.cardId;
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.boards.detail(receipt.boardId) }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.boards.contents(receipt.boardId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.boards.activity(receipt.boardId, 'all'),
    }),
    queryClient.invalidateQueries({
      queryKey: queryKeys.boards.activity(receipt.boardId, 'comments'),
    }),
    ...(cardId === undefined
      ? []
      : [
          queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.cards.activity(cardId, 'all'),
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.cards.activity(cardId, 'comments'),
          }),
        ]),
  ]);
}

async function getUndoSourceFeed(source: UndoSourceRegistration) {
  try {
    return source.scope.kind === 'card'
      ? await getCardActivity(source.scope.cardId, { filter: 'all', limit: 10 })
      : await getBoardActivity(source.scope.boardId, { filter: 'all', limit: 10 });
  } catch (error) {
    warnUndoSourceMiss(source, error);
    return { entries: [], nextCursor: null };
  }
}

function getBoardState(boardId: string): BoardUndoState {
  const cached = boardStates.get(boardId);
  if (cached !== undefined) return cached;
  const stored = readStoredBoardState(boardId);
  const state: BoardUndoState = {
    undoStack: [...stored.undoStack],
    redoStack: [...stored.redoStack],
    undoBusy: false,
    redoBusy: false,
    toast: null,
  };
  boardStates.set(boardId, state);
  return state;
}

function subscribeBoard(boardId: string, onStoreChange: () => void): () => void {
  if (typeof window !== 'undefined') {
    const onEvent = (event: Event) => {
      if (
        event instanceof CustomEvent &&
        isUndoChangeDetail(event.detail) &&
        event.detail.boardId === boardId
      ) {
        onStoreChange();
      }
    };
    window.addEventListener(CHANGE_EVENT, onEvent);
    return () => window.removeEventListener(CHANGE_EVENT, onEvent);
  }

  const listeners = serverListeners.get(boardId) ?? new Set<() => void>();
  listeners.add(onStoreChange);
  serverListeners.set(boardId, listeners);
  return () => {
    listeners.delete(onStoreChange);
    if (listeners.size === 0) serverListeners.delete(boardId);
  };
}

function notifyBoard(boardId: string): void {
  revisions.set(boardId, (revisions.get(boardId) ?? 0) + 1);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { boardId } }));
    return;
  }
  for (const listener of serverListeners.get(boardId) ?? []) listener();
}

function isUndoChangeDetail(value: unknown): value is { readonly boardId: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { readonly boardId?: unknown }).boardId === 'string'
  );
}

function readStoredBoardState(boardId: string): StoredBoardUndoState {
  if (typeof sessionStorage === 'undefined') return emptyStoredBoardState();
  try {
    const value = sessionStorage.getItem(storageKey(boardId));
    if (value === null) return emptyStoredBoardState();
    const parsed: unknown = JSON.parse(value);
    if (!isStoredBoardUndoState(parsed)) return emptyStoredBoardState();
    return parsed;
  } catch {
    return emptyStoredBoardState();
  }
}

function persistBoardState(boardId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  const state = getBoardState(boardId);
  const stored: StoredBoardUndoState = {
    undoStack: state.undoStack,
    redoStack: state.redoStack,
  };
  sessionStorage.setItem(storageKey(boardId), JSON.stringify(stored));
}

function emptyStoredBoardState(): StoredBoardUndoState {
  return { undoStack: [], redoStack: [] };
}

function isStoredBoardUndoState(value: unknown): value is StoredBoardUndoState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { undoStack?: unknown; redoStack?: unknown };
  return (
    Array.isArray(candidate.undoStack) &&
    Array.isArray(candidate.redoStack) &&
    candidate.undoStack.every(isUndoStackEntry) &&
    candidate.redoStack.every(isUndoStackEntry)
  );
}

function isUndoStackEntry(value: unknown): value is UndoStackEntry {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as {
    activityEntryId?: unknown;
    boardId?: unknown;
    cardId?: unknown;
    sourceKey?: unknown;
    label?: unknown;
  };
  return (
    typeof candidate.activityEntryId === 'string' &&
    typeof candidate.boardId === 'string' &&
    (candidate.cardId === undefined || typeof candidate.cardId === 'string') &&
    (candidate.sourceKey === undefined || typeof candidate.sourceKey === 'string') &&
    typeof candidate.label === 'string'
  );
}

function storageKey(boardId: string): string {
  return `${STORAGE_PREFIX}${boardId}`;
}

/**
 * The label with whatever terminal punctuation it arrived with removed, so that
 * the catalogue's frame supplies all of it.
 *
 * A BEHAVIOUR CHANGE, and a deliberate one. `punctuate` used to leave a label
 * ending in `!` or `?` exactly as it found it and append a full stop otherwise,
 * which put the decision "what ends this sentence" in two places: here for one
 * class of label and in the label itself for the other. A catalogue cannot
 * translate a frame it only sometimes owns. No call site in the workspace
 * produces a label ending in terminal punctuation - all nine come from
 * `undo.source.*`, none of which ends in one - so the change is invisible today
 * and is asserted in `undo.test.ts` rather than left to be discovered.
 */
function stripTerminal(label: string): string {
  return label.replace(/[.!?]\s*$/, '');
}

function recordedMessage(t: LocaleFormattingTranslate, label: string): string {
  return t('undo.toast.recorded', { label: stripTerminal(label) });
}

/**
 * Two keys, chosen by a discriminant - NOT one frame with a `{mode}` slot. The
 * participle is a word that inflects, and a frame that takes it as a slot
 * cannot inflect with it; `lib/dnd.ts` states the rule and
 * `boardView.pin`/`boardView.unpin` is the precedent in this same catalogue.
 */
function completionMessage(
  t: LocaleFormattingTranslate,
  label: string,
  mode: 'undone' | 'redone',
): string {
  return mode === 'undone'
    ? t('undo.toast.undone', { label: stripTerminal(label) })
    : t('undo.toast.redone', { label: stripTerminal(label) });
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim() !== '' ? error.message : fallback;
}

/**
 * ⚠ NOT `import.meta.env.DEV`-gated, and the guard's removal is the fix.
 *
 * `discoverUndoSource` returns without pushing anything when the activity feed
 * answers with no matching row — a slow feed, a filtered one, a request that
 * failed. In production that produced NO stack entry, NO toast, NO Undo and NO
 * diagnostic: the member's action succeeded, the affordance the requirement
 * promises simply never appeared, and nothing anywhere was red. A development
 * -only warning is the one place a defect can hide from both the member and
 * the operator at once.
 *
 * ⚠ WHAT THIS DELIBERATELY DOES NOT DO is say anything to the member.
 * `FR-EXP-013`'s edge cases specify what the product says for an EMPTY STACK
 * ("Nothing to undo" rather than doing nothing silently) and for a REFUSED
 * undo or redo — both of them at undo time, after someone has asked. A failed
 * RECORDING happens at action time, before anyone has asked, and no
 * requirement in the corpus covers it. Minting a sentence for it would be
 * reconstructing a clause that has no source. Reported as a corpus gap in
 * `docs/design/fe15-undo-affordance/AUDIT.md` §7 instead.
 *
 * The signature is unchanged and nothing is returned: making `recordSource`
 * answer with an outcome would change it at call sites in `CardBack.tsx` and
 * `ListHeader.tsx`, and the observability this buys does not need it.
 */
function warnUndoSourceMiss(source: UndoSourceRegistration, error?: unknown): void {
  console.warn(
    `No undo activity source found for ${source.label}. Expected one of: ${source.eventTypes.join(', ')}`,
    error,
  );
}
